import type { MailLinkDto } from "../../../services/mailLinkService";
import type { DocumentDto } from "../../../services/documentService";
import type { CallLogDto } from "../../../services/telephonyService";
import type { AggregatedNoteDto } from "../../../services/vorgangNoteService";
import type { VorgangTerminDto } from "../../../services/calendarTerminService";
import { toGraphDate } from "../../../utils/graphDatum";
import { graphAllDayDate } from "../../calendar/allDay";
import type { ParsedMailFile } from "../../../services/mailFileService";

/**
 * Der chronologische Vorgangsverlauf — REINE Ableitung, bewusst ohne React und
 * ohne Fetch (vitest `environment: node`, s. verlauf.test.ts).
 *
 * Seit PO 08.09.2026 zeigt die Achse NUR noch, was von außen kam: Mails,
 * Anrufe und Dokumente. Notizen und Kommentare stehen in der Blockfläche
 * darüber — was jemand geschrieben hat, gehört in die Notiz, nicht zwischen
 * die Ereignisse. Dieselbe Trennung, die „Notiz oben, Verlauf unten" meint.
 *
 * Seit PO 14.09.2026 gehört der TERMIN dazu: „Termin-Notiz, die einem Vorgang
 * zugewiesen wird, erscheint im Vorgang." Er ist genauso von außen gekommen
 * wie Mail, Anruf und Dokument — die Notiz-Seite lebt am Kalender-Sidecar,
 * hat Autosave und einen Zeichen-Layer; eine Kopie in `VorgangNote` veraltete
 * nach dem nächsten Tastendruck. Deshalb wird sie hier NUR verknüpft
 * (`ereignisBloecke.ts`: „Warum nicht als Notiz").
 */

export type VerlaufArt = "mail" | "anruf" | "dokument" | "termin";

/**
 * Ein Eintrag der Zeitachse. Die Nutzlast liegt in genau einem der vier
 * Felder — welches, sagt `art`. Ein diskriminiertes Union wäre typsicherer,
 * zwingt die Aufrufer aber zu Fallunterscheidungen auch dort, wo sie nur `at`
 * oder `todoId` brauchen (Sortierung, Gruppierung, Zählung).
 */
export interface VerlaufEintrag {
    /** Stabiler React-Schlüssel; über die Arten hinweg eindeutig. */
    key: string;
    art: VerlaufArt;
    /** Zeitpunkt des Ereignisses — Sortierschlüssel UND Anzeige. */
    at: Date;
    /** Zugehöriges To-do; null = hängt direkt am Vorgang. */
    todoId: number | null;
    mail?: MailLinkDto;
    /**
     * Die übrigen Mails derselben Unterhaltung in diesem Strang, jüngste zuerst
     * (Rücksprache 06.10.2026) — die Karte zeigt die jüngste, diese stehen
     * aufklappbar darin. Fehlt bei einer Mail, die allein steht.
     */
    unterhaltung?: MailLinkDto[];
    anruf?: CallLogDto;
    /**
     * Anruf: die Zuordnung, die DIESEN Eintrag trägt (PO 01.10.2026: ein Anruf
     * kann an mehreren To-dos und Vorgängen hängen — je Zuordnung im Vorgang
     * ein Eintrag). `null` = nur aus den Spalten abgeleitet (Altbestand).
     */
    zuordnungId?: number | null;
    dokument?: DocumentDto;
    /**
     * Frühere Fassungen dieses Dokuments — gleichnamige Dateien im selben
     * Strang, jüngste zuerst (Rücksprache 06.10.2026: „die Datei soll nur
     * einmal angezeigt werden und in ihr sind ja die Fassungen").
     */
    fassungen?: DocumentDto[];
    /** Geparste lokale `.eml`/`.msg`; `null` bedeutet unlesbar/Fallback. */
    mailDatei?: ParsedMailFile | null;
    termin?: VorgangTerminDto;
}

export interface VerlaufQuellen {
    /**
     * Der Vorgang, dessen Verlauf gebaut wird — wählt aus den Zuordnungen
     * eines Anrufs die dieses Vorgangs aus. Ohne Angabe gilt der Spiegel
     * `vorgangId`/`todoId` (ein Eintrag je Anruf, wie vor dem 01.10.2026).
     */
    vorgangId?: number;
    mails?: MailLinkDto[];
    /**
     * Die Notizen des Arbeitsstrangs. `baueVerlauf` wertet sie seit
     * PO 08.09.2026 NICHT mehr aus — sie bleiben im Typ, weil derselbe
     * Ladepfad sie holt und die Blockfläche über der Achse daraus entsteht.
     */
    notizen?: AggregatedNoteDto[];
    anrufe?: CallLogDto[];
    dokumente?: DocumentDto[];
    /**
     * Nur V4 setzt diese Map. Ein vorhandener Schlüssel klassifiziert das
     * Dokument fachlich als Mail; `null` hält es auch bei Parserfehlern dort.
     */
    mailDateien?: Record<number, ParsedMailFile | null>;
    /**
     * Die Termine des Vorgangs (`by-vorgang`, mit `notizSeite`). Jeder wird
     * Eintrag, über den sich etwas sagen lässt — siehe {@link terminImVorgang}.
     */
    termine?: VorgangTerminDto[];
    /** Vertreter-Mail → übrige Mails der Unterhaltung (`fasseUnterhaltungenZusammen`). */
    unterhaltungVon?: ReadonlyMap<number, MailLinkDto[]>;
    /** Vertreter-Dokument → frühere Fassungen (`fasseFassungenZusammen`). */
    fassungenVon?: ReadonlyMap<number, DocumentDto[]>;
}

/**
 * Trägt dieser Termin eine Notiz? Nur, wenn auf der Notiz-Seite etwas steht —
 * Text oder Zeichnung. Eine Seite, deren Text leergeräumt und deren Zeichnung
 * geleert wurde, zählt nicht: „leergeräumt heißt gelöscht" (PO 13.09.2026)
 * gilt hier wie am Notizblock.
 *
 * `notizSeite` ist auch null, wenn der Betrachter das Postfach nicht lesen
 * darf — dann gibt es für ihn hier nichts zu zeigen, und das ist richtig so.
 *
 * Bewusst ohne DOM (node-Runner): ein Tag-Strip genügt, um „steht da Text"
 * zu beantworten; der Auszug für die Karte entsteht in `ereignisBloecke.ts`.
 */
export function terminHatNotiz(
    t: Pick<VorgangTerminDto, "notizSeite"> & Partial<Pick<VorgangTerminDto, "vertraulich" | "notizVorhanden">>,
): boolean {
    // T-220: Ein vertraulicher Termin eines Kollegen mit Notiz verschwindet
    // nicht still — er wird ein Eintrag, der sagt, warum man nichts sieht.
    if (t.vertraulich) return t.notizVorhanden === true;
    const seite = t.notizSeite;
    if (!seite) return false;
    if (seite.hatZeichnung) return true;
    const text = (seite.contentHtml ?? "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .trim();
    return text.length > 0;
}

/**
 * Wird dieser Termin ein Eintrag im Vorgang? Jeder verknüpfte Termin — mit
 * oder ohne Notiz. Bis zum 27.10.2026 kamen nur Termine MIT Notiz in den Strom
 * (PO 14.09.2026); der User: „Termine, die mit Vorgängen verknüpft sind und
 * keine Notiz haben, sollten natürlich trotzdem im Vorgang angezeigt werden."
 *
 * Weg bleibt nur ein Termin, über den sich nichts sagen lässt: keine Notiz,
 * kein Betreff, keine Zeit — etwa weil der Termin in Outlook gelöscht wurde
 * und nur die Verknüpfung übrig ist. Ein vertraulicher Termin zählt immer;
 * seine Karte nennt nur „Vertraulicher Termin" und den Zeitraum (T-220).
 */
export function terminImVorgang(
    t: Pick<VorgangTerminDto, "notizSeite" | "subject" | "start"> &
        Partial<Pick<VorgangTerminDto, "vertraulich" | "notizVorhanden">>,
): boolean {
    if (t.vertraulich) return true;
    return terminHatNotiz(t) || !!t.subject?.trim() || !!t.start;
}

/**
 * Terminbeginn als Date. Graph liefert `start.dateTime` OHNE Zonen-Suffix
 * (UTC gemeint) — `toGraphDate` hängt das Z an; ohne das läse der Browser
 * Ortszeit und der Termin stünde um den Zonen-Versatz verschoben im Strom.
 * Ganztägige Termine kommen als Mitternacht UTC und zählen nur mit ihrem
 * Datumsteil (`graphAllDayDate`), sonst stünden sie um 02:00 im Strom.
 * Ohne Startzeit (kein Kalender-Leserecht) → Epoche, wie bei `zeit()`.
 */
function terminBeginn(t: Pick<VorgangTerminDto, "start" | "isAllDay">): Date {
    if (!t.start) return new Date(0);
    const d = t.isAllDay ? graphAllDayDate(t.start) : toGraphDate(t.start);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

/**
 * ISO-String → Date. Unlesbare und leere Werte werden zur Epoche (0), damit sie
 * ans Ende der absteigenden Sortierung rutschen statt die Liste zu sprengen.
 */
function zeit(iso?: string | null): Date {
    if (!iso) return new Date(0);
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

/** Baut die Zeitachse: neueste zuerst. */
export function baueVerlauf(quellen: VerlaufQuellen): VerlaufEintrag[] {
    const out: VerlaufEintrag[] = [];

    for (const m of quellen.mails ?? []) {
        out.push({
            key: `mail-${m.id}`,
            art: "mail",
            // `receivedAt` ist der fachliche Zeitpunkt; fehlt er (Alt-Links),
            // tritt der Verknüpfungszeitpunkt ein.
            at: zeit(m.receivedAt ?? m.linkedAt),
            todoId: m.todoId ?? null,
            mail: m,
            ...(quellen.unterhaltungVon?.get(m.id)?.length
                ? { unterhaltung: quellen.unterhaltungVon.get(m.id) }
                : {}),
        });
    }

    // Ein Eintrag je ZUORDNUNG im Vorgang (PO 01.10.2026): Hängt ein Anruf an
    // To-do 1 und To-do 2, steht er in beiden Strängen. Der Schlüssel trägt
    // deshalb das To-do mit. Ohne Zuordnungen im DTO (ältere Antwort) gilt der
    // Spiegel — ein Eintrag.
    for (const c of quellen.anrufe ?? []) {
        const hier =
            quellen.vorgangId != null && Array.isArray(c.zuordnungen)
                ? c.zuordnungen.filter((z) => z.vorgangId === quellen.vorgangId)
                : [];
        const ziele: Array<{ todoId: number | null; zuordnungId: number | null }> = hier.length
            ? hier.map((z) => ({ todoId: z.todoId ?? null, zuordnungId: z.id }))
            : [{ todoId: c.todoId ?? null, zuordnungId: null }];
        for (const z of ziele) {
            out.push({
                key: `anruf-${c.id}-${z.todoId ?? "v"}`,
                art: "anruf",
                at: zeit(c.startedAt),
                todoId: z.todoId,
                anruf: c,
                zuordnungId: z.zuordnungId,
            });
        }
    }

    for (const d of quellen.dokumente ?? []) {
        const istMailDatei = Object.prototype.hasOwnProperty.call(quellen.mailDateien ?? {}, d.id);
        const mailDatei = istMailDatei ? (quellen.mailDateien?.[d.id] ?? null) : undefined;
        out.push({
            key: `dokument-${d.id}`,
            art: istMailDatei ? "mail" : "dokument",
            at: zeit(mailDatei?.date ?? d.uploadedAt),
            todoId: d.todoId ?? null,
            dokument: d,
            ...(istMailDatei ? { mailDatei } : {}),
            ...(quellen.fassungenVon?.get(d.id)?.length ? { fassungen: quellen.fassungenVon.get(d.id) } : {}),
        });
    }

    for (const t of quellen.termine ?? []) {
        if (!terminImVorgang(t)) continue;
        out.push({
            key: `termin-${t.terminId}`,
            art: "termin",
            // Der TERMINBEGINN ist der fachliche Zeitpunkt, nicht der letzte
            // Autosave der Seite: die Notiz gehört zu dem Gespräch, das dann
            // stattfand — dort liest man sie im Strom.
            at: terminBeginn(t),
            // Aus dem Sidecar: so landet die Karte im richtigen Strang, und
            // die Erst-To-do-Übernahme (`planeTodoAnlage`) nimmt sie mit.
            todoId: t.todoId ?? null,
            termin: t,
        });
    }

    // Gleichstand deterministisch über den Schlüssel auflösen — sonst tanzen
    // gleichzeitige Einträge bei jedem Rendern, weil Array.sort instabil sein
    // darf und die Quellen-Reihenfolge zwischen Abrufen schwankt.
    return out.sort((a, b) => b.at.getTime() - a.at.getTime() || a.key.localeCompare(b.key));
}

/** Die Einträge EINES To-dos. */
export function verlaufFuerTodo(eintraege: VerlaufEintrag[], todoId: number): VerlaufEintrag[] {
    return eintraege.filter((e) => e.todoId === todoId);
}

/** Die Einträge OHNE To-do-Bezug — sie hängen direkt am Vorgang. */
export function verlaufOhneTodo(eintraege: VerlaufEintrag[]): VerlaufEintrag[] {
    return eintraege.filter((e) => e.todoId == null);
}

/**
 * Die Einträge der ZEITACHSE — alles außer Dokumenten.
 *
 * PO 04.09.2026: „Dokumente nicht im Verlauf anzeigen." Sie stehen weiter in
 * der Anhang-Leiste darüber; dort haben sie ihren Platz, in der Chronologie
 * verdrängten sie die Ereignisse.
 *
 * Gefiltert wird HIER und nicht in {@link baueVerlauf}: die Anhang-Leiste
 * bezieht ihre Chips über {@link dokumentEintraege} aus derselben Liste — wer
 * die Dokumente schon beim Bauen wegließe, ließe die Leiste leer laufen.
 */
export function achsenEintraege(eintraege: VerlaufEintrag[]): VerlaufEintrag[] {
    return eintraege.filter((e) => e.art !== "dokument");
}

/**
 * Die Dokument-Einträge als solche — mit `key` und `todoId`, anders als
 * {@link dokumenteAus}. Die Anhang-Leiste braucht das, seit ihre Chips die
 * Aktionen der Zeitachse tragen (zuweisen, auswählen).
 */
export function dokumentEintraege(eintraege: VerlaufEintrag[]): VerlaufEintrag[] {
    return eintraege.filter((e) => e.art === "dokument" && e.dokument);
}

/**
 * Die Dokumente eines Verlaufsabschnitts, neueste zuerst — Quelle der
 * Anhang-Leiste über der Zeitachse (Outlook-Muster: Anhänge stehen oben).
 */
export function dokumenteAus(eintraege: VerlaufEintrag[]): DocumentDto[] {
    return eintraege
        .filter((e) => e.art === "dokument" && e.dokument)
        .map((e) => e.dokument as DocumentDto);
}

/**
 * Zeitpunkt eines Verlaufseintrags: Tag, Monat und Uhrzeit — das JAHR nur,
 * wenn der Eintrag nicht aus dem laufenden Jahr stammt (PO 03.09.2026).
 * In einer Zeitachse, die fast nur dieses Jahr zeigt, ist die Jahreszahl
 * an jeder Zeile Lärm; fehlt sie, heißt das „dieses Jahr".
 *
 * `jetzt` ist injizierbar, damit der Test nicht am Kalender hängt.
 */
export function datumKurz(d: Date, jetzt: Date = new Date()): string {
    if (!d || Number.isNaN(d.getTime()) || d.getTime() === 0) return "ohne Datum";
    const tagMonat = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const jahr = d.getFullYear() === jetzt.getFullYear() ? "" : String(d.getFullYear());
    return `${tagMonat}${jahr} ${zeit}`;
}
